
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from './Reveal';
import { ArrowRight, Cpu } from 'lucide-react';
import { Page } from '../types';

interface ServiceRowProps {
  id: string;
  title: string;
  desc: string;
  features: string[];
  variant?: 'light' | 'dark';
  onClick?: () => void;
}

const ServiceRow: React.FC<ServiceRowProps> = ({ id, title, desc, features, variant = 'light', onClick }) => {
  const isDark = variant === 'dark';
  const { t } = useTranslation();

  return (
    <div
      className={`
        group relative border-t border-gray-200 py-12 md:py-20 transition-all duration-500 w-full cursor-pointer overflow-hidden
        ${isDark ? 'bg-[#0B1121] border-gray-800' : 'hover:bg-gray-50/50'}
      `}
      onClick={onClick}
    >
      {/* Hover Gradient Overlay (Subtle) */}
      {!isDark && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
      )}

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">

          {/* ID */}
          <div className={`md:col-span-1 text-xs font-mono pt-2 tracking-widest ${isDark ? 'text-gray-600' : 'text-gray-400 group-hover:text-clever-teal transition-colors'}`}>
            {id}
          </div>

          {/* Title */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              {isDark && <Cpu className="text-clever-teal animate-pulse-slow" size={28} />}
              <h3 className={`text-2xl md:text-3xl font-bold transition-all duration-300 group-hover:translate-x-2 ${isDark ? 'text-white' : 'text-gray-900 group-hover:text-clever-teal'}`}>
                {title}
              </h3>
            </div>
          </div>

          {/* Description & Features */}
          <div className={`md:col-span-5 leading-relaxed text-base md:text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <p className="mb-6 md:mb-8">{desc}</p>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {features.map((feat, i) => (
                <span
                  key={i}
                  className={`text-xs md:text-sm font-medium px-3 py-1 rounded-full transition-all duration-300 ${isDark
                      ? 'text-white bg-white/10 hover:bg-white/20'
                      : 'text-gray-600 bg-gray-100 group-hover:bg-white group-hover:shadow-sm'
                    }`}
                >
                  {feat}
                </span>
              ))}
            </div>
          </div>

          {/* Arrow (Hidden on Mobile, Visible on Desktop) */}
          <div className="md:col-span-2 hidden md:flex justify-end pt-2">
            <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-[-20px] group-hover:translate-x-0">
              <ArrowRight className={`${isDark ? 'text-white' : 'text-clever-teal'} w-5 h-5`} />
            </div>
          </div>

          {/* Mobile Arrow Hint */}
          <div className="md:hidden flex items-center gap-2 mt-4 text-xs font-bold uppercase tracking-widest text-clever-teal opacity-0 group-hover:opacity-100 transition-opacity">
            Details <ArrowRight size={12} />
          </div>

        </div>
      </div>
    </div>
  );
};

interface ServicesGridProps {
  onNavigate?: (page: Page) => void;
}

export const ServicesGrid: React.FC<ServicesGridProps> = ({ onNavigate }) => {
  const { t } = useTranslation();

  const services = [
    {
      id: "01",
      title: t('shared.services_grid.items.01.title'),
      desc: t('shared.services_grid.items.01.desc'),
      features: t('shared.services_grid.items.01.features', { returnObjects: true }) as string[],
      link: 'medicine' as Page
    },
    {
      id: "02",
      title: t('shared.services_grid.items.02.title'),
      desc: t('shared.services_grid.items.02.desc'),
      features: t('shared.services_grid.items.02.features', { returnObjects: true }) as string[],
      link: 'law' as Page
    },
    {
      id: "03",
      title: t('shared.services_grid.items.03.title'),
      desc: t('shared.services_grid.items.03.desc'),
      features: t('shared.services_grid.items.03.features', { returnObjects: true }) as string[],
      link: 'finance' as Page
    },
    {
      id: "04",
      title: t('shared.services_grid.items.04.title'),
      desc: t('shared.services_grid.items.04.desc'),
      features: t('shared.services_grid.items.04.features', { returnObjects: true }) as string[],
      link: 'trade' as Page
    },
    {
      id: "05",
      title: t('shared.services_grid.items.05.title'),
      desc: t('shared.services_grid.items.05.desc'),
      features: t('shared.services_grid.items.05.features', { returnObjects: true }) as string[],
      link: 'craft' as Page
    },
    {
      id: "06",
      title: t('shared.services_grid.items.06.title'),
      desc: t('shared.services_grid.items.06.desc'),
      features: t('shared.services_grid.items.06.features', { returnObjects: true }) as string[],
      link: 'estate' as Page
    },
    {
      id: "07",
      title: t('shared.services_grid.items.07.title'),
      desc: t('shared.services_grid.items.07.desc'),
      features: t('shared.services_grid.items.07.features', { returnObjects: true }) as string[],
      link: 'gastronomy' as Page
    },
    {
      id: "08",
      title: t('shared.services_grid.items.08.title'),
      desc: t('shared.services_grid.items.08.desc'),
      features: t('shared.services_grid.items.08.features', { returnObjects: true }) as string[],
      variant: 'dark',
      link: 'custom-architecture' as Page
    }
  ];

  return (
    <section id="services" className="py-20 md:py-40 bg-white">
      <div className="max-w-7xl mx-auto px-6">

        <Reveal>
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 md:mb-24 border-b border-gray-900 pb-8">
            <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none mb-4 md:mb-0 uppercase">
              {t('shared.services_grid.title_line1')}<br />{t('shared.services_grid.title_line2')}
            </h2>
            <p className="text-gray-500 max-w-md text-left md:text-right text-base md:text-lg">
              {t('shared.services_grid.subtitle')}
            </p>
          </div>
        </Reveal>
      </div>

      <div className="flex flex-col w-full">
        {services.map((service, index) => (
          <Reveal key={service.id} delay={index * 0.05} width="100%">
            <ServiceRow
              {...service}
              variant={service.variant as 'light' | 'dark'}
              onClick={() => service.link && onNavigate ? onNavigate(service.link) : null}
            />
          </Reveal>
        ))}
      </div>

    </section>
  );
};
